// src/lib/impositions.js
// ───────────────────────────────────────────────────────────────────────────
// 🧩 Gang Run / Combos — la IMPOSICIÓN como artefacto de primera clase
// (PLAN-ARCHIVOS-PREPRESS §5). Una imposición puede llevar piezas de VARIOS
// productos, jobs y clientes en un mismo pliego. Cada pieza referencia su origen
// {jobId, pieceCode, clientId}; el QR por pieza (ya codifica {jobId,pieceCode})
// mantiene la trazabilidad aunque compartan pliego. Costeo prorrateado por ups.
// ───────────────────────────────────────────────────────────────────────────
import { collection, addDoc, doc, updateDoc, runTransaction, serverTimestamp } from "firebase/firestore";

export const IMPOSITION_STATUSES = ["abierta", "impresa", "cortada", "cerrada"];

/** Código consecutivo de gang run (GR-00001). PURO sobre el número. */
export function makeImpositionCode(n) {
  return `GR-${String(n || 0).padStart(5, "0")}`;
}

/** Reserva el siguiente número de imposición (crea el contador si no existe). */
async function nextNumber(db) {
  const counterRef = doc(db, "counters", "impositions");
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const next = (snap.exists() ? snap.data().lastNumber || 0 : 0) + 1;
    if (snap.exists()) tx.update(counterRef, { lastNumber: next });
    else tx.set(counterRef, { lastNumber: next });
    return next;
  });
}

/** Normaliza una pieza colocada en la imposición. PURO. */
export function buildPiece(p) {
  return {
    jobId: p.jobId || null,
    jobNumber: p.jobNumber || null,
    clientId: p.clientId || null,
    clientName: p.clientName || "",
    skuRef: p.skuRef || null,
    pieceCode: (p.pieceCode || "").trim(),
    pieceName: p.pieceName || "",
    qty: Number(p.qty) || 1,
    ups: Number(p.ups) || 1,
  };
}

/**
 * 🧮 Calculadora de imposición / nesting (grid simple). PURO (testeable).
 * Cuántas piezas entran por pliego (probando orientación normal y rotada) y
 * cuántos pliegos imprimir/cortar para la cantidad pedida.
 * @param {object} p { pieceW, pieceH, sheetW, sheetH, margin, gap, qty }
 * @return {{upsPerSheet:number, sheets:number, orientation:string, wastePct:number}}
 */
export function calcImposition({ pieceW, pieceH, sheetW, sheetH, margin = 0, gap = 0, qty = 0 }) {
  const pw = Number(pieceW) || 0, ph = Number(pieceH) || 0;
  const sw = Number(sheetW) || 0, sh = Number(sheetH) || 0;
  const m = Number(margin) || 0, g = Number(gap) || 0;
  const uW = sw - 2 * m, uH = sh - 2 * m;
  const fit = (w, h) => {
    if (w <= 0 || h <= 0 || uW <= 0 || uH <= 0) return 0;
    const cols = Math.floor((uW + g) / (w + g));
    const rows = Math.floor((uH + g) / (h + g));
    return Math.max(0, cols) * Math.max(0, rows);
  };
  const upsN = fit(pw, ph);
  const upsR = fit(ph, pw);
  const upsPerSheet = Math.max(upsN, upsR);
  const orientation = upsR > upsN ? "rotada" : "normal";
  const q = Number(qty) || 0;
  const sheets = upsPerSheet > 0 && q > 0 ? Math.ceil(q / upsPerSheet) : 0;
  const eff = upsPerSheet > 0 && sw > 0 && sh > 0 ? (upsPerSheet * pw * ph) / (sw * sh) : 0;
  const wastePct = +((1 - Math.min(eff, 1)) * 100).toFixed(1);
  return { upsPerSheet, sheets, orientation, wastePct };
}

/** Prorratea un costo total entre las piezas por su peso (ups×qty). PURO (testeable). */
export function prorrateByUps(pieces, totalCost) {
  const weights = (pieces || []).map((p) => (Number(p.ups) || 1) * (Number(p.qty) || 1));
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  return (pieces || []).map((p, i) => ({ ...p, costShare: +((totalCost || 0) * (weights[i] / sum)).toFixed(2) }));
}

/** ¿La imposición es gang (piezas de >1 job)? PURO. */
export function isGang(pieces) {
  return new Set((pieces || []).map((p) => p.jobId).filter(Boolean)).size > 1;
}

/** Crea una imposición nueva (código automático). */
export async function createImposition(db, payload, user) {
  const n = await nextNumber(db);
  const code = makeImpositionCode(n);
  const ref = await addDoc(collection(db, "impositions"), {
    code,
    number: n,
    status: "abierta",
    materialId: payload.materialId || null,
    materialName: payload.materialName || "",
    sheet: payload.sheet || { width: null, height: null, unit: "in" },
    notes: payload.notes || "",
    pieces: [],
    createdAt: serverTimestamp(),
    createdBy: user?.uid || null,
  });
  return { id: ref.id, code };
}

/** Reemplaza la lista de piezas (agregar/quitar se resuelven en el cliente). */
export async function setPieces(db, impositionId, pieces) {
  await updateDoc(doc(db, "impositions", impositionId), { pieces: (pieces || []).map(buildPiece) });
}

/** Cambia el estado de la imposición (se propaga a los jobs en una fase posterior). */
export async function setImpositionStatus(db, impositionId, status) {
  await updateDoc(doc(db, "impositions", impositionId), { status });
}
